import React, { useState, useEffect, useMemo } from 'react';
import {
  treasuryService,
  TreasuryTransaction,
  TreasuryStats,
  TransactionType,
  TransactionVault,
  VAULT_LABELS,
  INCOME_CATEGORIES,
  EXPENSE_CATEGORIES,
} from '../../services/treasuryService';
import {
  CreditCard,
  TrendingUp,
  PlusCircle,
  RefreshCw,
  Printer,
  Search,
  ArrowRight,
  ArrowLeft,
  Building2,
  Trash2,
  CheckCircle2,
  AlertCircle,
  FileText,
  X,
} from 'lucide-react';
import { AdminLoadMore } from './AdminLoadMore';
import './AdminTreasury.css';

export const AdminTreasury: React.FC = () => {
  const [transactions, setTransactions] = useState<TreasuryTransaction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);

  // Filters & Visibility (15 initial + 10 more)
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<'ALL' | TransactionType>('ALL');
  const [vaultFilter, setVaultFilter] = useState<'ALL' | TransactionVault>('ALL');
  const [timeFilter, setTimeFilter] = useState<'ALL' | 'TODAY' | 'WEEK' | 'MONTH'>('ALL');
  const [visibleCount, setVisibleCount] = useState<number>(15);

  // Form State
  const [formData, setFormData] = useState<{
    type: TransactionType;
    category: string;
    amount: string;
    vault: TransactionVault;
    description: string;
    referenceNumber: string;
    recordedBy: string;
    date: string;
  }>({
    type: 'INCOME',
    category: 'BOOKING_PAYMENT',
    amount: '',
    vault: 'CASH_BRANCH',
    description: '',
    referenceNumber: '',
    recordedBy: 'مدير النظام',
    date: new Date().toISOString().split('T')[0],
  });

  const [formFeedback, setFormFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const fetchTransactions = async () => {
    setIsLoading(true);
    try {
      const data = await treasuryService.getAllTransactions();
      setTransactions(data);
    } catch (err) {
      console.error('Failed to load treasury data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const stats: TreasuryStats = useMemo(() => {
    return treasuryService.calculateStats(transactions);
  }, [transactions]);

  // Filtered list
  const filteredTransactions = useMemo(() => {
    return transactions.filter((tx) => {
      // Type filter
      if (typeFilter !== 'ALL' && tx.type !== typeFilter) return false;

      // Vault filter
      if (vaultFilter !== 'ALL' && tx.vault !== vaultFilter) return false;

      // Time filter
      if (timeFilter !== 'ALL') {
        const txDate = new Date(tx.date).getTime();
        const now = Date.now();
        if (timeFilter === 'TODAY') {
          const oneDayAgo = now - 24 * 3600 * 1000;
          if (txDate < oneDayAgo) return false;
        } else if (timeFilter === 'WEEK') {
          const sevenDaysAgo = now - 7 * 24 * 3600 * 1000;
          if (txDate < sevenDaysAgo) return false;
        } else if (timeFilter === 'MONTH') {
          const thirtyDaysAgo = now - 30 * 24 * 3600 * 1000;
          if (txDate < thirtyDaysAgo) return false;
        }
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesDesc = tx.description?.toLowerCase().includes(q);
        const matchesRef = tx.referenceNumber?.toLowerCase().includes(q);
        const matchesBy = tx.recordedBy?.toLowerCase().includes(q);
        const matchesCat = tx.categoryLabelAr?.toLowerCase().includes(q);
        if (!matchesDesc && !matchesRef && !matchesBy && !matchesCat) return false;
      }

      return true;
    });
  }, [transactions, typeFilter, vaultFilter, timeFilter, searchQuery]);

  // Reset pagination on filter change
  useEffect(() => {
    setVisibleCount(15);
  }, [typeFilter, vaultFilter, timeFilter, searchQuery]);

  const visibleTransactions = useMemo(() => {
    return filteredTransactions.slice(0, visibleCount);
  }, [filteredTransactions, visibleCount]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormFeedback(null);

    const numAmount = parseFloat(formData.amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setFormFeedback({ type: 'error', message: 'يرجى إدخال مبلغ صحيح أكبر من الصفر' });
      return;
    }

    if (!formData.description.trim()) {
      setFormFeedback({ type: 'error', message: 'يرجى كتابة بيان ووصف واضح للحركة المالية' });
      return;
    }

    setIsSubmitting(true);
    try {
      const catList = formData.type === 'INCOME' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
      const matchedCat = catList.find((c) => c.id === formData.category);
      const catLabel = matchedCat ? matchedCat.label : 'حركة مالية عامة';

      const newTx = await treasuryService.addTransaction({
        type: formData.type,
        category: formData.category,
        categoryLabelAr: catLabel,
        amount: numAmount,
        vault: formData.vault,
        description: formData.description.trim(),
        referenceNumber: formData.referenceNumber.trim() || undefined,
        recordedBy: formData.recordedBy.trim() || 'مدير النظام',
        date: new Date(formData.date).toISOString(),
        status: 'CONFIRMED',
      });

      setTransactions((prev) => [newTx, ...prev]);
      setShowAddModal(false);
      setFormData({
        type: 'INCOME',
        category: 'BOOKING_PAYMENT',
        amount: '',
        vault: 'CASH_BRANCH',
        description: '',
        referenceNumber: '',
        recordedBy: 'مدير النظام',
        date: new Date().toISOString().split('T')[0],
      });
    } catch (err: any) {
      setFormFeedback({ type: 'error', message: err?.message || 'تعذر حفظ الحركة المالية' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('هل أنت متأكد من رغبتك في حذف هذه الحركة المالية من سجل الخزينة؟')) {
      return;
    }
    try {
      await treasuryService.deleteTransaction(id);
      setTransactions((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      console.error('Failed to delete transaction:', err);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="admin-treasury-view" dir="rtl">
      {/* 1. Executive Treasury Header */}
      <div className="treasury-header-card">
        <div className="treasury-header-glow" />
        <div className="treasury-header-top">
          <div>
            <div className="treasury-badge-live">
              <span className="treasury-live-dot" />
              <span>نظام الخزينة والسيولة النقدية المباشر</span>
            </div>
            <h1 className="treasury-title">خزينة المنظومة والتعاملات المالية</h1>
            <p className="treasury-subtitle">
              إدارة التدفقات النقدية اللحظية، تسوية متحصلات الطلاب والاشتراكات، صرف مستحقات التشغيل والسولار، ومطابقة المحافظ الإلكترونية.
            </p>
          </div>

          <div className="treasury-header-actions">
            <button
              onClick={() => {
                setFormData((prev) => ({
                  ...prev,
                  type: 'INCOME',
                  category: INCOME_CATEGORIES[0].id,
                }));
                setShowAddModal(true);
              }}
              className="btn-treasury-income"
            >
              <PlusCircle size={18} />
              <span>تسجيل توريد (إيراد) +</span>
            </button>

            <button
              onClick={() => {
                setFormData((prev) => ({
                  ...prev,
                  type: 'EXPENSE',
                  category: EXPENSE_CATEGORIES[0].id,
                }));
                setShowAddModal(true);
              }}
              className="btn-treasury-expense"
            >
              <PlusCircle size={18} />
              <span>تسجيل صرف (مصروف) -</span>
            </button>

            <button onClick={fetchTransactions} className="btn-treasury-tool" title="تحديث البيانات">
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              <span>تحديث</span>
            </button>

            <button onClick={handlePrint} className="btn-treasury-tool" title="طباعة كشف الحساب">
              <Printer size={16} />
              <span>طباعة الكشف</span>
            </button>
          </div>
        </div>
      </div>

      {/* 2. Master Liquidity & KPI Showcase Grid */}
      <div className="treasury-kpi-showcase">
        {/* Master Titanium Card */}
        <div className="treasury-master-card">
          <div className="master-card-header">
            <span className="master-card-label">صافي رصيد الخزينة المتاح (NET CASH)</span>
            <span className="master-card-chip">LIVE VAULT</span>
          </div>
          <div className="master-card-balance">
            {stats.netBalance.toLocaleString()} <span>ج.م</span>
          </div>
          <div className="master-card-footer">
            <div style={{ width: '100%' }}>
              <div className="master-flow-bar">
                <div
                  className="master-flow-fill-income"
                  style={{
                    width: `${
                      stats.totalIncome + stats.totalExpense > 0
                        ? (stats.totalIncome / (stats.totalIncome + stats.totalExpense)) * 100
                        : 100
                    }%`,
                  }}
                />
                <div
                  className="master-flow-fill-expense"
                  style={{
                    width: `${
                      stats.totalIncome + stats.totalExpense > 0
                        ? (stats.totalExpense / (stats.totalIncome + stats.totalExpense)) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94A3B8' }}>
                <span>نسبة السيولة الواردة مقارنة بالمصروفات</span>
                <span style={{ color: '#34D399', fontWeight: 800 }}>
                  {stats.totalIncome + stats.totalExpense > 0
                    ? Math.round((stats.totalIncome / (stats.totalIncome + stats.totalExpense)) * 100)
                    : 100}%
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Total Inflows (Income) */}
        <div className="treasury-flow-card income-card">
          <div className="flow-card-top">
            <span className="flow-card-title">إجمالي المقبوضات (الإيرادات)</span>
            <div className="flow-card-icon">
              <TrendingUp size={22} />
            </div>
          </div>
          <div className="flow-card-value">
            +{stats.totalIncome.toLocaleString()} <span>ج.م</span>
          </div>
          <div className="flow-card-sub">
            مجموع تحصيلات الحجوزات والاشتراكات والإيداعات المعتمدة
          </div>
        </div>

        {/* Total Outflows (Expense) */}
        <div className="treasury-flow-card expense-card">
          <div className="flow-card-top">
            <span className="flow-card-title">إجمالي المصروفات التشغيلية</span>
            <div className="flow-card-icon">
              <TrendingUp size={22} style={{ transform: 'rotate(180deg)' }} />
            </div>
          </div>
          <div className="flow-card-value">
            -{stats.totalExpense.toLocaleString()} <span>ج.م</span>
          </div>
          <div className="flow-card-sub">
            مجموع وقود وسولار الأتوبيسات، مستحقات السائقين، الصيانة، والكارتات
          </div>
        </div>
      </div>

      {/* 3. Sub-Vaults Breakdown Cards */}
      <div className="treasury-vaults-section">
        <div className="vaults-section-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Building2 size={18} style={{ color: '#64748B' }} />
            <span>توزيع أرصدة الخزينة حسب طريقة وحساب الاستلام المعتمد:</span>
          </div>
          <span style={{ fontSize: '0.78rem', color: '#94A3B8' }}>
            مطابقة فورية لكافة القنوات
          </span>
        </div>
        <div className="vaults-cards-grid">
          <div className="vault-mini-card cash">
            <div className="vault-mini-icon">
              <Building2 size={20} />
            </div>
            <div className="vault-mini-meta">
              <span className="vault-mini-name">خزينة الفرع (الكاش)</span>
              <span className="vault-mini-amount">
                {stats.vaultBalances.cashBranch.toLocaleString()} ج.م
              </span>
            </div>
          </div>

          <div className="vault-mini-card instapay">
            <div className="vault-mini-icon">
              <CreditCard size={20} />
            </div>
            <div className="vault-mini-meta">
              <span className="vault-mini-name">حساب إنستاباي البنكي</span>
              <span className="vault-mini-amount">
                {stats.vaultBalances.instapay.toLocaleString()} ج.م
              </span>
            </div>
          </div>

          <div className="vault-mini-card vodafone">
            <div className="vault-mini-icon">
              <CreditCard size={20} />
            </div>
            <div className="vault-mini-meta">
              <span className="vault-mini-name">محفظة فودافون كاش</span>
              <span className="vault-mini-amount">
                {stats.vaultBalances.vodafoneCash.toLocaleString()} ج.م
              </span>
            </div>
          </div>

          <div className="vault-mini-card other">
            <div className="vault-mini-icon">
              <Building2 size={20} />
            </div>
            <div className="vault-mini-meta">
              <span className="vault-mini-name">باقي المحافظ والحسابات</span>
              <span className="vault-mini-amount">
                {stats.vaultBalances.otherVaults.toLocaleString()} ج.م
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Filters & Search Bar */}
      <div className="treasury-toolbar">
        {/* Search Input */}
        <div className="treasury-search-box">
          <Search size={18} className="treasury-search-icon" />
          <input
            type="text"
            placeholder="بحث بالبيان، رقم الإيصال، اسم المسؤول، أو الفئة..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="treasury-search-input"
          />
        </div>

        {/* Filter Pills */}
        <div className="treasury-filter-group">
          {/* Type Filter */}
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as any)}
            className="treasury-select-pill"
          >
            <option value="ALL">كل الحركات (إيراد + مصروف)</option>
            <option value="INCOME">مقبوضات وتوريدات فقط 🟢</option>
            <option value="EXPENSE">مصروفات وسحوبات فقط 🔴</option>
          </select>

          {/* Vault Filter */}
          <select
            value={vaultFilter}
            onChange={(e) => setVaultFilter(e.target.value as any)}
            className="treasury-select-pill"
          >
            <option value="ALL">كافة الخزائن والحسابات</option>
            <option value="CASH_BRANCH">خزينة الفرع (كاش)</option>
            <option value="INSTAPAY">إنستاباي (حساب بنكي)</option>
            <option value="VODAFONE_CASH">فودافون كاش</option>
            <option value="ORANGE_CASH">أورنج كاش</option>
            <option value="ETISALAT_CASH">اتصالات كاش</option>
            <option value="WE_PAY">وي باي (WE Pay)</option>
          </select>

          {/* Time Filter */}
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e.target.value as any)}
            className="treasury-select-pill"
          >
            <option value="ALL">كل الفترات الزمنية</option>
            <option value="TODAY">اليوم فقط</option>
            <option value="WEEK">آخر 7 أيام</option>
            <option value="MONTH">هذا الشهر</option>
          </select>
        </div>
      </div>

      {/* 5. Transactions Ledger Table */}
      <div className="treasury-ledger-card">
        <div className="ledger-header">
          <div className="ledger-header-title">
            <FileText size={18} style={{ color: '#E5232A' }} />
            <span>سجل ودفتر قيود الخزينة العام</span>
            <span className="ledger-count-chip">{filteredTransactions.length} حركة</span>
          </div>
          <span style={{ fontSize: '0.78rem', color: '#94A3B8' }}>
            مرتبة من الأحدث إلى الأقدم
          </span>
        </div>

        {isLoading ? (
          <div style={{ padding: '4rem 1rem', textAlign: 'center', color: '#94A3B8' }}>
            <RefreshCw size={32} className="animate-spin" style={{ margin: '0 auto 0.75rem', color: '#E5232A' }} />
            <p style={{ fontWeight: 700, fontSize: '0.95rem' }}>جاري تحميل ومزامنة حركات الخزينة...</p>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div style={{ padding: '4rem 1rem', textAlign: 'center', color: '#94A3B8' }}>
            <CreditCard size={48} style={{ margin: '0 auto 0.75rem', opacity: 0.3 }} />
            <p style={{ fontWeight: 800, fontSize: '1rem', color: '#475569' }}>لا توجد حركات مالية مطابقة للبحث</p>
            <p style={{ fontSize: '0.82rem', marginTop: '0.25rem' }}>يمكنك تسجيل حركة توريد أو صرف جديدة الآن</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="ledger-table">
              <thead>
                <tr>
                  <th>نوع الحركة</th>
                  <th>التاريخ والوقت</th>
                  <th>البيان والتفاصيل</th>
                  <th>الفئة المحاسبية</th>
                  <th>الخزينة / الحساب</th>
                  <th>رقم الإيصال / المرجع</th>
                  <th>المسؤول المفوّض</th>
                  <th style={{ textAlign: 'left' }}>المبلغ</th>
                  <th style={{ textAlign: 'center' }}>إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {visibleTransactions.map((tx) => (
                  <tr key={tx.id} className="ledger-row">
                    {/* Type Badge */}
                    <td>
                      {tx.type === 'INCOME' ? (
                        <span className="tx-type-pill income">
                          <ArrowLeft size={13} />
                          <span>توريد (إيراد)</span>
                        </span>
                      ) : (
                        <span className="tx-type-pill expense">
                          <ArrowRight size={13} />
                          <span>صرف (مصروف)</span>
                        </span>
                      )}
                    </td>

                    {/* Date */}
                    <td style={{ fontSize: '0.78rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                      {new Date(tx.date).toLocaleDateString('ar-EG', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>

                    {/* Description */}
                    <td>
                      <div className="tx-desc-title">{tx.description}</div>
                      <div className="tx-desc-meta">
                        <span>كود القيد: #{tx.id.substring(0, 8)}</span>
                      </div>
                    </td>

                    {/* Category */}
                    <td>
                      <span className="tx-cat-chip">
                        {tx.categoryLabelAr || tx.category}
                      </span>
                    </td>

                    {/* Vault */}
                    <td>
                      <span className="tx-vault-badge">
                        {VAULT_LABELS[tx.vault] || tx.vault}
                      </span>
                    </td>

                    {/* Ref Number */}
                    <td style={{ fontFamily: 'monospace', fontSize: '0.82rem', color: '#64748B' }}>
                      {tx.referenceNumber || '—'}
                    </td>

                    {/* Recorded By */}
                    <td style={{ fontSize: '0.82rem', fontWeight: 600, color: '#475569' }}>
                      {tx.recordedBy}
                    </td>

                    {/* Amount */}
                    <td style={{ textAlign: 'left' }}>
                      <span className={`tx-amount-display ${tx.type === 'INCOME' ? 'income' : 'expense'}`}>
                        {tx.type === 'INCOME' ? '+' : '-'}
                        {Number(tx.amount).toLocaleString()} ج.م
                      </span>
                    </td>

                    {/* Actions */}
                    <td style={{ textAlign: 'center' }}>
                      <button
                        onClick={() => handleDelete(tx.id)}
                        className="btn-tx-delete"
                        title="حذف الحركة"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredTransactions.length > 0 && (
              <AdminLoadMore
                currentCount={Math.min(visibleCount, filteredTransactions.length)}
                totalCount={filteredTransactions.length}
                itemName="حركة مالية"
                onLoadMore={() => setVisibleCount((prev) => prev + 10)}
                step={10}
                onShowAll={() => setVisibleCount(filteredTransactions.length)}
              />
            )}
          </div>
        )}
      </div>

      {/* 6. Add Transaction Modal */}
      {showAddModal && (
        <div
          className="treasury-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowAddModal(false);
          }}
        >
          <div className="treasury-modal-box">
            {/* Modal Header */}
            <div className="treasury-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <PlusCircle size={20} style={{ color: '#E5232A' }} />
                <h3>تسجيل حركة مالية جديدة في الخزينة</h3>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="btn-modal-close"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} className="treasury-modal-form">
              {formFeedback && (
                <div
                  style={{
                    padding: '0.75rem 1rem',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    background: formFeedback.type === 'error' ? '#FEF2F2' : '#ECFDF5',
                    color: formFeedback.type === 'error' ? '#991B1B' : '#065F46',
                    border: `1px solid ${formFeedback.type === 'error' ? '#FECACA' : '#A7F3D0'}`,
                  }}
                >
                  <AlertCircle size={16} />
                  <span>{formFeedback.message}</span>
                </div>
              )}

              {/* Transaction Type Switch */}
              <div>
                <label className="modal-label">نوع الحركة المالية:</label>
                <div className="modal-type-switcher">
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        type: 'INCOME',
                        category: INCOME_CATEGORIES[0].id,
                      });
                    }}
                    className={`btn-modal-type ${formData.type === 'INCOME' ? 'active-income' : ''}`}
                  >
                    <ArrowLeft size={16} />
                    <span>توريد / إيداع (إيراد)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        type: 'EXPENSE',
                        category: EXPENSE_CATEGORIES[0].id,
                      });
                    }}
                    className={`btn-modal-type ${formData.type === 'EXPENSE' ? 'active-expense' : ''}`}
                  >
                    <ArrowRight size={16} />
                    <span>صرف / سحب (مصروف)</span>
                  </button>
                </div>
              </div>

              {/* Amount & Vault */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                <div>
                  <label className="modal-label">المبلغ المطلوب (ج.م): *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="1"
                    required
                    placeholder="مثال: 500"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="modal-input-field"
                    style={{ fontWeight: 800, fontSize: '1.05rem' }}
                  />
                </div>

                <div>
                  <label className="modal-label">الخزينة أو الحساب المستهدف: *</label>
                  <select
                    value={formData.vault}
                    onChange={(e) => setFormData({ ...formData, vault: e.target.value as any })}
                    className="modal-input-field"
                  >
                    <option value="CASH_BRANCH">خزينة الفرع (كاش)</option>
                    <option value="INSTAPAY">إنستاباي (حساب بنكي)</option>
                    <option value="VODAFONE_CASH">فودافون كاش</option>
                    <option value="ORANGE_CASH">أورنج كاش</option>
                    <option value="ETISALAT_CASH">اتصالات كاش</option>
                    <option value="WE_PAY">وي باي (WE Pay)</option>
                    <option value="BANK_ACCOUNT">تحويل بنكي مباشر</option>
                  </select>
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="modal-label">الفئة المحاسبية: *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="modal-input-field"
                >
                  {(formData.type === 'INCOME' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="modal-label">البيان والوصف التفصيلي: *</label>
                <textarea
                  rows={2}
                  required
                  placeholder="اكتب سبب الحركة أو تفاصيل التحصيل / المصروف..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="modal-input-field"
                />
              </div>

              {/* Reference Number & Recorded By */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.85rem' }}>
                <div>
                  <label className="modal-label">رقم الإيصال / المرجع (اختياري):</label>
                  <input
                    type="text"
                    placeholder="مثال: REC-9921"
                    value={formData.referenceNumber}
                    onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
                    className="modal-input-field"
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>

                <div>
                  <label className="modal-label">المسؤول القائم بالعملية: *</label>
                  <input
                    type="text"
                    required
                    value={formData.recordedBy}
                    onChange={(e) => setFormData({ ...formData, recordedBy: e.target.value })}
                    className="modal-input-field"
                  />
                </div>
              </div>

              {/* Date */}
              <div>
                <label className="modal-label">تاريخ الحركة: *</label>
                <input
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="modal-input-field"
                />
              </div>

              {/* Modal Buttons */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '0.75rem',
                  marginTop: '0.5rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #E2E8F0',
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{
                    padding: '0.65rem 1.25rem',
                    borderRadius: '12px',
                    border: '1px solid #CBD5E1',
                    background: 'white',
                    color: '#475569',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-treasury-income"
                  style={{
                    background:
                      formData.type === 'INCOME'
                        ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                        : 'linear-gradient(135deg, #DC2626 0%, #B9141D 100%)',
                  }}
                >
                  {isSubmitting ? <RefreshCw size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                  <span>تثبيت وقيد الحركة الآن</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
